import { PropertyTypeEnum } from '~/core/entities/CRMEnums';
import { PropertyFiltersPresenter } from '~/presenters/PropertyFiltersPresenter';
import { slugify } from '~~/utils';
import { getRegionPath } from '~~/utils/regionPath';

export interface Region {
	title: string;
	description: string;
	image?: string;
	url?: string;
	categories?: CategoryConfig[];
}

// Interface for regions used in StandardSlider component
export interface RegionForSlider {
	id: string;
	name: string;
	image: string;
	properties: number;
}

export interface CategoryConfig {
	title: string;
	propertyType: PropertyTypeEnum;
	image?: string;
	url?: string;
}

interface CreateRegionParams {
	title: string;
	description: string;
	city?: string;
	state?: string;
	country?: string;
	categoriesConfig?: CategoryConfig[];
}

const regionImagesBaseURL = 'https://d37vfq26jjkkpt.cloudfront.net/bairros';

const defaultCategoriesConfig: CategoryConfig[] = [
	{ title: 'Apartamento', propertyType: PropertyTypeEnum.APARTMENT },
	{ title: 'Casa', propertyType: PropertyTypeEnum.HOUSE },
	{ title: 'Cobertura', propertyType: PropertyTypeEnum.PENTHOUSE }
];

function createRegion(params: CreateRegionParams): Region {
	const {
		title,
		description,
		city = 'São Paulo',
		state = 'SP',
		country = 'Brasil',
		categoriesConfig = defaultCategoriesConfig
	} = params;

	const titleFormatted = slugify(title);
	const image = `${regionImagesBaseURL}/${titleFormatted}/banner.webp`;
	const url = getRegionPath({ name: title, city, state, country });

	const categories = categoriesConfig.map(category =>
		createCategoryData(title, category)
	);

	return {
		title,
		description,
		image,
		url,
		categories
	};
}

function createCategoryData(
	regionTitle: string,
	category: CategoryConfig
): CategoryConfig {
	const categoryTitleFormatted = slugify(category.title);
	const image =
		category.image ??
		`/images/regions/default/property-types/${categoryTitleFormatted}.webp`;

	const { propertyType } = category;
	const url = PropertyFiltersPresenter.getPathFromFilters({
		propertyTypes: [propertyType],
		regions: [regionTitle],
		states: ['SP'],
		cities: ['São Paulo']
	});

	return { title: category.title, image, url, propertyType };
}

export const regions: Region[] = [
	createRegion({
		title: 'Itaim Bibi',
		description:
			'Sua conexão entre vida pessoal e profissional está em um dos bairros mais procurados e queridos de São Paulo.',
		categoriesConfig: [
			{
				title: 'Apartamento',
				propertyType: PropertyTypeEnum.APARTMENT
			},
			{
				title: 'Cobertura',
				propertyType: PropertyTypeEnum.PENTHOUSE
			},
			{
				title: 'Duplex',
				propertyType: PropertyTypeEnum.DUPLEX
			}
		]
	}),
	createRegion({
		title: 'Jardim Paulista',
		description:
			'Onde a energia de São Paulo se encontra com o verde do parque do Ibirapuera.'
	}),
	createRegion({
		title: 'Vila Nova Conceição',
		description:
			'Sofisticação, natureza e qualidade de vida em um dos bairros mais desejados de São Paulo – tudo isso perto do seu escritório.',
		categoriesConfig: [
			{
				title: 'Apartamento',
				propertyType: PropertyTypeEnum.APARTMENT
			},
			{
				title: 'Casa',
				propertyType: PropertyTypeEnum.HOUSE
			},
			{
				title: 'Cobertura',
				propertyType: PropertyTypeEnum.PENTHOUSE
			}
		]
	}),
	createRegion({
		title: 'Jardim América',
		description:
			'Aos pés da Oscar Freire, um dos bairros mais cobiçados e exclusivos de São Paulo.',
		categoriesConfig: [
			{
				title: 'Apartamento',
				propertyType: PropertyTypeEnum.APARTMENT
			},
			{
				title: 'Casa',
				propertyType: PropertyTypeEnum.HOUSE
			},
			{
				title: 'Cobertura',
				propertyType: PropertyTypeEnum.PENTHOUSE
			}
		]
	}),
	createRegion({
		title: 'Pinheiros',
		description:
			'Momentos de lazer, alta gastronomia e ótimos drinks estão no bairro mais procurado de São Paulo.',
		categoriesConfig: [
			{
				title: 'Apartamento',
				propertyType: PropertyTypeEnum.APARTMENT
			},
			{
				title: 'Cobertura',
				propertyType: PropertyTypeEnum.PENTHOUSE
			},
			{
				title: 'Casa',
				propertyType: PropertyTypeEnum.HOUSE
			}
		]
	}),
	createRegion({
		title: 'Alto de Pinheiros',
		description: 'Um refúgio no coração da zona oeste paulistana.',
		categoriesConfig: [
			{
				title: 'Casa',
				propertyType: PropertyTypeEnum.HOUSE
			},
			{
				title: 'Apartamento',
				propertyType: PropertyTypeEnum.APARTMENT
			},
			{
				title: 'Casa em condomínio',
				propertyType: PropertyTypeEnum.CONDO_HOUSE
			}
		]
	}),
	createRegion({
		title: 'Jardim Europa',
		description:
			'Mergulhe na sofisticação de um dos bairros mais cobiçados de São Paulo.',
		categoriesConfig: [
			{
				title: 'Apartamento',
				propertyType: PropertyTypeEnum.APARTMENT
			},
			{
				title: 'Casa',
				propertyType: PropertyTypeEnum.HOUSE
			},
			{
				title: 'Casa em condomínio',
				propertyType: PropertyTypeEnum.CONDO_HOUSE
			}
		]
	}),
	createRegion({
		title: 'Jardim Paulistano',
		description:
			'Seu oásis verde entre as avenidas mais movimentadas de São Paulo.',
		categoriesConfig: [
			{
				title: 'Apartamento',
				propertyType: PropertyTypeEnum.APARTMENT
			},
			{
				title: 'Casa',
				propertyType: PropertyTypeEnum.HOUSE
			},
			{
				title: 'Casa em condomínio',
				propertyType: PropertyTypeEnum.CONDO_HOUSE
			}
		]
	})
];

/**
 * Mock regions for the StandardSlider component
 */
export const mockRegionsForSlider: RegionForSlider[] = [
	{
		id: '1',
		name: 'Brooklin',
		image: 'https://placehold.co/640x480?text=Brooklin',
		properties: 120
	},
	{
		id: '2',
		name: 'Campo Belo',
		image: 'https://placehold.co/640x480?text=Campo+Belo',
		properties: 85
	},
	{
		id: '3',
		name: 'Chácara Santo Antônio',
		image: 'https://placehold.co/640x480?text=Chacara+Santo+Antonio',
		properties: 64
	},
	{
		id: '4',
		name: 'Moema',
		image: 'https://placehold.co/640x480?text=Moema',
		properties: 150
	},
	{
		id: '5',
		name: 'Vila Olímpia',
		image: 'https://placehold.co/640x480?text=Vila+Olimpia',
		properties: 95
	},
	{
		id: '6',
		name: 'Jardim Europa',
		image: 'https://placehold.co/640x480?text=Jardim+Europa',
		properties: 72
	}
];

/**
 * Generate a specific number of regions for the StandardSlider component
 * @param count The number of regions to generate
 * @returns An array of RegionForSlider objects
 */
export function generateMockRegionsForSlider(
	count: number = 6
): RegionForSlider[] {
	// If requesting 6 or fewer regions, return the static mockRegionsForSlider array
	if (count <= mockRegionsForSlider.length) {
		return mockRegionsForSlider.slice(0, count);
	}

	// If we need more regions than we have, generate additional ones
	const additionalNeeded = count - mockRegionsForSlider.length;
	const additionalRegions: RegionForSlider[] = Array.from(
		{ length: additionalNeeded },
		(_, index) => {
			const regionIndex = mockRegionsForSlider.length + index + 1;
			return {
				id: regionIndex.toString(),
				name: `Region ${regionIndex}`,
				image: `https://placehold.co/640x480?text=Region+${regionIndex}`,
				properties: Math.floor(Math.random() * 100) + 50 // Random number between 50 and 150
			};
		}
	);

	return [...mockRegionsForSlider, ...additionalRegions];
}
